import {
  type ContractDraftTarget,
  useContractDrafts,
  useOpenContractDraftPdf
} from '@services/contracts/drafts'
import { format, parseISO } from 'date-fns'
import { CircleCheck, CircleX, FileText, History } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const SHOWN = 3

/** Every send of the contract draft: when, by whom, to whom, and the PDF. */
export function ContractDraftHistory({
  target
}: {
  target: ContractDraftTarget
}) {
  const { t } = useTranslation('contract')
  const drafts = useContractDrafts(target)
  const openPdf = useOpenContractDraftPdf()
  const [showAll, setShowAll] = useState(false)

  const list = drafts.data ?? []
  if (drafts.isLoading) {
    return <div className="h-12 animate-pulse rounded-xl bg-ink/[0.04]" />
  }

  const shown = showAll ? list : list.slice(0, SHOWN)

  return (
    <div className="space-y-2">
      <h4 className="m-0 flex items-center gap-2 text-sm font-semibold text-ink">
        <History className="h-4 w-4 text-primary" />
        {t('draft_history.title')}
      </h4>

      {list.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          {t('draft_history.empty')}
        </p>
      ) : (
        <ul className="m-0 list-none space-y-2 p-0">
          {shown.map((draft) => {
            const sentCount = draft.recipients.filter((r) => r.sent).length
            const status =
              sentCount === draft.recipients.length
                ? 'sent'
                : sentCount === 0
                  ? 'failed'
                  : 'partial'
            return (
              <li
                key={draft.id}
                className="space-y-1.5 rounded-xl border border-ink/[0.08] bg-ink/[0.015] px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-semibold text-ink">
                      {format(parseISO(draft.created_at), 'd. M. yyyy HH:mm')}
                    </span>
                    <span
                      className={cn(
                        'ml-2 rounded-full px-2 py-0.5 text-xs font-semibold',
                        status === 'sent' && 'bg-success-soft text-success',
                        status === 'partial' && 'bg-peach/15 text-peach',
                        status === 'failed' &&
                          'bg-destructive/10 text-destructive'
                      )}
                    >
                      {t(`draft_history.status_${status}`)}
                    </span>
                    {draft.sent_by_name && (
                      <span className="ml-2 text-muted-foreground">
                        {t('draft_history.sent_by', {
                          name: draft.sent_by_name
                        })}
                      </span>
                    )}
                  </div>
                  {draft.has_document && (
                    <Button
                      size="sm"
                      variant="ghostDark"
                      onClick={() => openPdf.mutate(draft.id)}
                      disabled={openPdf.isPending}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {t('draft_history.open_pdf')}
                    </Button>
                  )}
                </div>
                <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs">
                  {draft.recipients.map((r) => (
                    <li
                      key={r.email}
                      className="flex items-center gap-1 text-muted-foreground"
                      title={r.sent ? undefined : t('draft_history.not_sent')}
                    >
                      {r.sent ? (
                        <CircleCheck className="h-3.5 w-3.5 text-success" />
                      ) : (
                        <CircleX className="h-3.5 w-3.5 text-destructive" />
                      )}
                      {r.name ? `${r.name} · ${r.email}` : r.email}
                    </li>
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
      )}

      {list.length > SHOWN && (
        <Button
          size="sm"
          variant="ghostDark"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? t('draft_history.show_less')
            : t('draft_history.show_all', { count: list.length })}
        </Button>
      )}
    </div>
  )
}
